import { useState } from 'react';
import { toast } from 'sonner';
import { UserCircle, Crown, Trash2, LogOut, UserPlus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useCollaborators, useRemoveCollaborator, useLeaveAlbum } from '@/hooks/useCollaborators';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import type { AlbumCollaborator } from '@omokage/shared';

interface CollaboratorsPanelProps {
  albumId: string;
  onInviteClick: () => void;
}

interface CollaboratorItemProps {
  collaborator: AlbumCollaborator;
  isCurrentUser: boolean;
  isOwner: boolean;
  currentUserIsOwner: boolean;
  onRemove: (userId: string) => void;
}

function CollaboratorItem({
  collaborator,
  isCurrentUser,
  isOwner,
  currentUserIsOwner,
  onRemove,
}: CollaboratorItemProps) {
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  const displayName = collaborator.displayName || '匿名ユーザー';
  const joinedDate = new Date(collaborator.joinedAt * 1000).toLocaleDateString();

  return (
    <>
      <div
        className={cn(
          'flex items-center justify-between p-3 rounded-lg border',
          'bg-white/50 dark:bg-card/50 border-border',
          'hover:bg-white/80 dark:hover:bg-card/80 transition-colors'
        )}
      >
        <div className="flex items-center gap-3">
          {/* Avatar */}
          {collaborator.avatarUrl ? (
            <img
              src={collaborator.avatarUrl}
              alt={displayName}
              className="w-10 h-10 rounded-full object-cover"
              onError={(e) => {
                // Hide failed image and show fallback icon
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            className="w-10 h-10 rounded-full bg-muted flex items-center justify-center"
            style={{ display: collaborator.avatarUrl ? 'none' : 'flex' }}
          >
            <UserCircle className="w-6 h-6 text-muted-foreground" />
          </div>

          {/* User info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
              {isCurrentUser && (
                <span className="text-xs text-blue-600 dark:text-blue-400">（あなた）</span>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                {isOwner && <Crown className="h-3 w-3 text-warning" />}
                {isOwner ? 'オーナー' : 'メンバー'}
              </span>
              <span>•</span>
              <span>参加日 {joinedDate}</span>
            </div>
          </div>
        </div>

        {/* Actions */}
        {!isOwner && currentUserIsOwner && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRemoveDialog(true)}
            className="text-destructive hover:bg-destructive/10"
            aria-label="コラボレーターを削除"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Remove confirmation dialog */}
      <ConfirmDialog
        open={showRemoveDialog}
        onOpenChange={setShowRemoveDialog}
        title="コラボレーターを削除"
        description={`${displayName} をこのアルバムから削除しますか？このアルバム内のすべての写真へのアクセスが失われます。`}
        onConfirm={() => {
          onRemove(collaborator.userId);
          setShowRemoveDialog(false);
        }}
        confirmText="削除"
        variant="destructive"
      />
    </>
  );
}

function CollaboratorSkeleton() {
  return (
    <div className="space-y-2">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-16 w-full" />
    </div>
  );
}

export function CollaboratorsPanel({ albumId, onInviteClick }: CollaboratorsPanelProps) {
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);

  const { data: collaborators, isLoading } = useCollaborators(albumId);
  const { user } = useAuth();
  const removeCollaborator = useRemoveCollaborator();
  const leaveAlbum = useLeaveAlbum();

  const currentUserRole = collaborators?.find((c) => c.userId === user?.id)?.role;
  const isOwner = currentUserRole === 'owner';

  const handleRemove = async (userId: string) => {
    try {
      await removeCollaborator.mutateAsync({ albumId, userId });
      toast.success('コラボレーターを削除しました');
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'コラボレーターの削除に失敗しました';
      toast.error(errorMessage);
    }
  };

  const handleLeave = async () => {
    try {
      await leaveAlbum.mutateAsync(albumId);
      toast.success('アルバムから退出しました');
      // Navigation will be handled by the parent component
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'アルバムからの退出に失敗しました';
      toast.error(errorMessage);
    }
  };

  if (isLoading) {
    return (
      <Card className="bg-white/80 dark:bg-background/80 backdrop-blur-xl border-border/50">
        <CardHeader>
          <CardTitle>コラボレーター</CardTitle>
        </CardHeader>
        <CardContent>
          <CollaboratorSkeleton />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white/80 dark:bg-background/80 backdrop-blur-xl border-border/50">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>コラボレーター</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {collaborators?.length || 0}人のメンバー
            </p>
          </div>
          <div className="flex gap-2">
            {isOwner && (
              <Button
                variant="outline"
                size="sm"
                onClick={onInviteClick}
                aria-label="メンバーを招待"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                招待
              </Button>
            )}
            {!isOwner && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowLeaveDialog(true)}
                className="text-destructive"
                aria-label="アルバムから退出"
              >
                <LogOut className="mr-2 h-4 w-4" />
                アルバムから退出
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!collaborators || collaborators.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>コラボレーターはいません</p>
              <p className="text-sm mt-1">このアルバムにメンバーを招待してください</p>
            </div>
          ) : (
            <div className="space-y-2">
              {collaborators.map((collaborator) => (
                <CollaboratorItem
                  key={collaborator.userId}
                  collaborator={collaborator}
                  isCurrentUser={collaborator.userId === user?.id}
                  isOwner={collaborator.role === 'owner'}
                  currentUserIsOwner={isOwner}
                  onRemove={handleRemove}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Leave album confirmation */}
      <ConfirmDialog
        open={showLeaveDialog}
        onOpenChange={setShowLeaveDialog}
        title="アルバムから退出"
        description="このアルバムから退出しますか？このアルバム内のすべての写真へのアクセスが失われます。"
        onConfirm={handleLeave}
        confirmText="退出"
        variant="destructive"
      />
    </>
  );
}
